import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Trophy, Users, Zap, Crown, Plus, ArrowRight } from "lucide-react";
import Navbar, { UserAvatar } from "../components/Navbar";
import Footer from "../components/Footer";
import { api, resolveAsset } from "../lib/api";
import { useAuth } from "../lib/auth";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

export default function ProfilePage() {
  const { user, loading } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (user) api.get("/me/profile").then((r) => setProfile(r.data));
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  if (!user) return <Navigate to="/" replace />;

  const stats = profile?.stats || { tournaments_created: 0, tournaments_joined: 0, wins: 0, losses: 0 };
  const created = profile?.created || [];
  const joined = profile?.joined || [];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero card */}
        <div className="glass-red-glow rounded-2xl p-6 md:p-8 mb-8 flex flex-col md:flex-row items-center md:items-start gap-6">
          <UserAvatar user={user} size={96} />
          <div className="flex-1 text-center md:text-left">
            <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-1">Profile</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">
              {user.username || user.name}
            </h1>
            <div className="text-sm text-zinc-400 mt-1">{user.email}</div>
            {user.role === "admin" && (
              <span className="inline-block mt-2 text-[10px] uppercase font-mono tracking-widest text-red-300 bg-red-950/50 border border-red-500/30 rounded-full px-2 py-0.5">
                Admin
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/settings" data-testid="profile-settings-btn" className="btn-ghost-glass !py-2 !px-4 !text-xs">
              Edit profile
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatTile icon={Trophy} label="Created" value={stats.tournaments_created} testid="stat-created" />
          <StatTile icon={Users} label="Joined" value={stats.tournaments_joined} testid="stat-joined" />
          <StatTile icon={Crown} label="Wins" value={stats.wins} tone="emerald" testid="stat-wins" />
          <StatTile icon={Zap} label="Losses" value={stats.losses} tone="red" testid="stat-losses" />
        </div>

        {/* Tournaments created */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-2xl">Tournaments you host</h2>
            {user.role === "admin" && (
              <Link to="/tournaments" data-testid="profile-new-tournament" className="btn-liquid !py-2 !px-4 !text-xs">
                <Plus size={12} /> New tournament
              </Link>
            )}
          </div>
          <TournamentList
            items={created}
            empty={user.role === "admin" ? "You haven't created a tournament yet." : "Only admins can create tournaments."}
          />
        </section>

        {/* Tournaments joined */}
        <section>
          <h2 className="font-display font-bold text-2xl mb-4">Tournaments you've joined</h2>
          <TournamentList items={joined} empty="You haven't joined any tournaments yet." />
        </section>
      </div>
      <Footer discordUrl={DISCORD_URL} />
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone = "default", testid }) {
  const color = tone === "emerald" ? "text-emerald-400" : tone === "red" ? "text-red-400" : "text-white";
  return (
    <div data-testid={testid} className="glass-dark rounded-2xl p-5 flex items-center gap-3">
      <div className="h-10 w-10 rounded-full glass-red flex items-center justify-center">
        <Icon size={16} className="text-red-300" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
        <div className={`font-display font-black text-2xl ${color}`}>{value}</div>
      </div>
    </div>
  );
}

function TournamentList({ items, empty }) {
  if (!items.length) {
    return <div className="glass-dark rounded-2xl p-8 text-center text-zinc-500">{empty}</div>;
  }
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {items.map((t) => (
        <Link
          key={t.id}
          to={`/tournaments/${t.id}`}
          data-testid={`profile-t-${t.id}`}
          className="glass-dark rounded-xl p-4 hover:border-red-500/40 transition-all flex items-center gap-3"
        >
          {t.banner_url ? (
            <img src={resolveAsset(t.banner_url)} alt="" className="h-12 w-20 rounded-lg object-cover border border-white/10" />
          ) : (
            <div className="h-12 w-20 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(230,0,0,0.35), rgba(20,0,0,0.8))" }}>
              <Trophy size={14} className="text-red-300" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{t.name}</div>
            <div className="text-xs text-zinc-400 font-mono uppercase tracking-widest">
              {t.status} · {t.game || t.format?.replace("_", " ")}
            </div>
          </div>
          <ArrowRight size={14} className="text-red-400" />
        </Link>
      ))}
    </div>
  );
}
